import type { FC } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { ConfirmationDialog, SelectApplications, SelectGroups, TextField } from 'components/common';
import { useEnqueueSnackbar, useScrollToError } from 'hooks';
import { useNavigate } from 'react-router';
import { useUpdateUser } from '../api';
import editUserValidator from '../validation/editUserValidator';
import type { z } from 'zod';
import type { User } from '../types';
import { useCheckAuth, UserRole } from 'features/login';
import { InfoOutlined } from '@mui/icons-material';
import { APP_LIST } from '../../../../constants';
import { SelectAccessLevel } from './UsersComponents';

type FormData = z.infer<typeof editUserValidator.schema>;

interface EditUserFormProps {
  data?: User;
}

const EditUserForm: FC<EditUserFormProps> = ({ data }) => {
  const navigate = useNavigate();
  const { onShowErrorAlert, onShowAlert } = useEnqueueSnackbar();
  const { role } = useCheckAuth();

  const isAvailableToEdit = role === UserRole.SUPERUSER;

  const updateUser = useUpdateUser({
    config: {
      onSuccess: () => {
        onShowAlert('User updated successfully');
        navigate('/cabinet/facebook/users');
      },
      onError: (error) => {
        onShowErrorAlert(error.message, { delayAlert: 7000 });
      },
    },
  });

  const formMethods = useForm<FormData>({
    resolver: editUserValidator.resolver,
    defaultValues: {
      username: data?.username ?? '',
      email: data?.email ?? '',
      groups: data?.groups.map(g => g.id) ?? [],
      apps: data?.role === UserRole.SUPERUSER ? APP_LIST.map(n => n.code) : (data?.apps ?? []),
      role: data?.role ?? UserRole.USER,
    },
  });

  const {
    handleSubmit,
    formState: { errors, isValid },
  } = formMethods;

  useScrollToError<FormData>({ errors });

  const onSubmit = (formData: FormData) => {
    if (!data?.id) return;

    const validationResult = editUserValidator.schema.safeParse(formData);
    if (!validationResult.success) {
      onShowErrorAlert('Please fix the form errors before submitting');
      return;
    }
    const validatedFormData = validationResult.data;

    updateUser.mutate({
      id: data.id,
      data: {
        groups: validatedFormData.groups,
        apps: validatedFormData.apps,
        is_staff: [UserRole.STAFF, UserRole.SUPERUSER].includes(validatedFormData.role),
        is_superuser: [UserRole.SUPERUSER].includes(validatedFormData.role),
      },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          maxWidth: '40%',
          alignItems: 'start',
          justifyContent: 'start',
        }}
      >
        <Typography variant={'subtitle1'}>Edit User Profile</Typography>
        {role !== UserRole.SUPERUSER && (
          <Box sx={{ display: 'flex', mb: 2 }}>
            <Alert color={'info'} icon={<InfoOutlined />}>
              <AlertTitle>
                Access restricted
              </AlertTitle>
              <Typography
                variant={'body2'}
                sx={t => ({ 'a': { color: t.palette.info.main }, 'a:hover': { textDecoration: 'none' } })}>
                Only Superusers can make changes to user accounts. Contact yours for support.
              </Typography>
            </Alert>
          </Box>
        )}

        <Tooltip title={'Not editable'} followCursor>
          <Box sx={{ display: 'flex', width: '100%' }}>
            <TextField
              name={'email'}
              inputLabel={'Email'}
              placeholder={'Value'}
              disabled
            />
          </Box>
        </Tooltip>

        <Typography variant="subtitle2">
          Access level
        </Typography>
        <SelectAccessLevel
          name={'role'}
          inputLabel={'Access level'}
          required
          disabled={!isAvailableToEdit}
        />
        <Typography variant="subtitle2">
          Networks and permissions
        </Typography>
        <SelectGroups
          name={'groups'}
          inputLabel={'Networks and permissions'}
          multiples
          required
          disabled={!isAvailableToEdit}
        />
        <Typography variant="subtitle2">
          Projects
        </Typography>
        <SelectApplications
          name={'apps'}
          inputLabel={'Projects'}
          multiples
          required
          disabled={!isAvailableToEdit}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Button
            startIcon={
              updateUser.isPending && (
                <CircularProgress color={'inherit'} size={18} />
              )
            }
            onClick={handleSubmit(onSubmit)}
            variant={'contained'}
            disabled={updateUser.isPending || !isAvailableToEdit || !isValid}
          >
            Update user
          </Button>
        </Box>
      </Box>
      <ConfirmationDialog isUnblock={updateUser.isSuccess} />
    </FormProvider>
  );
};

export default EditUserForm;
